import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Put the page back at the top when you navigate.
//
// React Router does not do this. A single-page app only swaps the components
// under the router — the document never reloads, so the scroll position from
// the last screen is simply still there when the next one renders.
//
// The symptom that found this: on the landing page, click "Features" (which
// scrolls to 961px), then click "Home". The URL went from /#features to / and
// the page did not move at all — the whole navigation was invisible.
//
// The worse case was one click further on. Going from the landing page to
// /about kept that same 961px, so the About page opened a thousand pixels down,
// below its own headline, looking like it started mid-sentence.
//
// THE THREE CASES
//
//   1. A #hash        → scroll to that section, always. Whether it arrived by
//                       link, by full page load, or by pressing Back, the URL
//                       names a section and that is where the visitor goes.
//   2. No hash, PUSH  → scroll to the top. The ordinary navigation this whole
//                       component exists for.
//   3. No hash, POP   → do nothing. Returning to a list you had scrolled
//                       halfway down should put you where you were, and the
//                       browser's own scroll restoration already does that.
//
// Deliberately written with no "is this the first render" flag. An earlier
// version had one, and it was wrong twice over: React Router reports the
// INITIAL PAGE LOAD as POP — the same value it uses for Back — and StrictMode
// invokes effects twice in development, so a ref flipped during the first
// invocation was already stale by the second. The first run scheduled the
// scroll, the cleanup cancelled it, and the second run took the POP branch and
// returned. Nothing moved, and nothing in the code looked wrong.
//
// The rules above need no such flag: each depends only on the current URL and
// navigation type, so running the effect twice does what running it once does.
// ---------------------------------------------------------------------------

export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (hash) {
      // The section is often not in the DOM yet. Following "FAQ" from /about is
      // a FULL page load — the browser resolves #faq before React has rendered
      // a single section — so a one-shot lookup finds nothing and the visitor
      // lands at the top of the page they asked to jump into.
      //
      // So it retries for up to a second, then gives up quietly rather than
      // scrolling to the top (wrong: a section was asked for) or polling
      // forever (wrong: the id may genuinely not exist).
      let frame;
      const giveUpAt = performance.now() + 1000;
      const findAndScroll = () => {
        // getElementById, not querySelector: an id beginning with a digit is a
        // valid id but an invalid CSS selector, and querySelector throws on it.
        const target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (performance.now() < giveUpAt) {
          frame = requestAnimationFrame(findAndScroll);
        }
      };
      findAndScroll();
      return () => cancelAnimationFrame(frame);
    }

    if (navigationType === 'POP') return undefined;

    // `key` is in the dependency list, not just pathname: clicking "Home" while
    // already on "/" changes neither the path nor the hash, and without it the
    // effect would not run for the exact click that exposed this.
    window.scrollTo({ top: 0, left: 0 });
    return undefined;
  }, [pathname, hash, key, navigationType]);

  return null;
}
